import { Injectable } from "@angular/core";
import Crypto from "crypto-js";

@Injectable()
export class LocalStorageService {
  setItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  getItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  clear(): void {
    localStorage.clear();
  }

  async crypt(otp: string, salt: string): Promise<ArrayBuffer> {
    const cryptoKey = await generateCryptoKey(otp, salt);

    const dataToEncrypt = new TextEncoder().encode(
      JSON.stringify(localStorage)
    );

    return await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: new ArrayBuffer(16),
        tagLength: 128
      },
      cryptoKey,
      dataToEncrypt
    );
  }

  async uncrypt(
    pin: string,
    salt: string,
    crypedData: ArrayBuffer
  ): Promise<string> {
    const otp = Crypto.algo.SHA256.create()
      .update(pin)
      .update(salt)
      .finalize()
      .toString(Crypto.enc.Base64);

    const cryptoKey = await generateCryptoKey(otp, salt);

    const decBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: new ArrayBuffer(16),
        tagLength: 128
      },
      cryptoKey,
      crypedData
    );

    return new TextDecoder().decode(decBuffer);
  }
}

async function generateCryptoKey(
  otp: string,
  salt: string
): Promise<CryptoKey> {
  const keyData = new TextEncoder().encode(otp);

  const keyToDerive = await window.crypto.subtle.importKey(
    "raw",
    keyData,
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return await window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: new TextEncoder().encode(salt),
      iterations: 250000,
      hash: "SHA-256"
    },
    keyToDerive,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}
